import { useEffect, useId, useRef } from "react";

/** A centred dialog over a dimmed page.

    Extracted from Benches.tsx when the run detail grew a delete of its own:
    two copies of a modal drift apart, and the second one is always the one
    that forgets the dark theme.

    IT WAS A `<div>`. No role, no aria-modal, no Escape, no focus trap, no way
    back out with the keyboard - which matters most here, because every caller
    is a destructive confirmation. Tab out of "Delete bench" and the focus ring
    is somewhere on the page behind the dim, still reachable, still clickable,
    with a dialog on top asking a question the reader can no longer answer.

    Four things, all of them table stakes:

     - `role="dialog" aria-modal` and a heading it points at, so a screen
       reader announces what is being asked rather than reading the page under
       it.
     - Escape and a click on the backdrop, because those are the two ways
       everybody tries to leave. Both are cancel, never confirm.
     - Focus moves INTO the dialog on open and is trapped there, so Tab cycles
       the question's own controls.
     - Focus goes back where it came from on close, so the list does not jump
       to the top and the keyboard reader does not lose their place.

    `onClose` is optional only so an older caller cannot break; pass it. Without
    it Escape and the backdrop do nothing, which is the behaviour this replaced.
 */
export default function Modal({
  title,
  children,
  onClose,
  wide = false,
}: {
  title: string;
  children: React.ReactNode;
  onClose?: () => void;
  /** A form, not a question: room for a list of choices on a wide screen. */
  wide?: boolean;
}) {
  const box = useRef<HTMLDivElement>(null);
  const titleId = useId();

  useEffect(() => {
    // Where focus was, so it can go back. Read once, on open.
    const from = document.activeElement as HTMLElement | null;
    const focusable = () =>
      Array.from(
        box.current?.querySelectorAll<HTMLElement>(
          'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])',
        ) ?? [],
      );

    // The first control, or the box itself when the dialog is only a
    // question with its buttons disabled until something is typed.
    (focusable()[0] ?? box.current)?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose?.();
        return;
      }
      if (e.key !== "Tab") return;
      const items = focusable();
      if (items.length === 0) {
        e.preventDefault();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const here = document.activeElement;
      // Wrap at both ends. Also catches focus that is outside the dialog
      // entirely, which is where it starts if the caller renders no controls.
      if (e.shiftKey && (here === first || !box.current?.contains(here))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && here === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("keydown", onKey, true);
      from?.focus?.();
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-10 flex items-end justify-center bg-black/40 p-4 sm:items-center"
      // The backdrop only, never a click that started inside the dialog and
      // ended on it - dragging to select text used to close the question.
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        ref={box}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        // NEVER TALLER THAN THE SCREEN. The start form grew a list of bench
        // images (29 Sep 2026) and on a phone its top went off the screen
        // with no way to scroll back up: the box was as tall as its content
        // inside a fixed backdrop. Now the box stops at the screen's height,
        // the title stays, and the content scrolls inside it.
        className={`flex max-h-[calc(100dvh-2rem)] w-full flex-col rounded-lg bg-white shadow-lg outline-none dark:bg-slate-900 ${
          wide ? "max-w-2xl" : "max-w-md"
        }`}
      >
        <h3 id={titleId} className="shrink-0 px-4 pb-3 pt-4 font-semibold">
          {title}
        </h3>
        <div className="min-h-0 overflow-y-auto overscroll-contain px-4 pb-4" data-testid="modal-body">
          {children}
        </div>
      </div>
    </div>
  );
}
